import { useAuthStore } from '@/stores/authStore'
import toast from 'react-hot-toast'
import { WEBSITE_ONLY_QUERY_PARAM } from '@/lib/categories'

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/v1/api'

// Export a utility function to get the API URL consistently
export const getApiUrl = () => process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/v1/api'

/**
 * Infer MIME type and media category when `File.type` is empty — common when picking videos
 * on iOS Safari (shows as ""). Without this, presign rejects missing contentType or treats video as image.
 */
export function inferChatMediaFromFile(file: File): { contentType: string; mediaType: 'image' | 'video' } | null {
  const raw = file.type?.trim()
  if (raw && raw !== 'application/octet-stream') {
    if (raw.startsWith('video/')) return { contentType: raw, mediaType: 'video' }
    if (raw.startsWith('image/')) return { contentType: raw, mediaType: 'image' }
  }
  const dot = file.name.lastIndexOf('.')
  const ext = dot >= 0 ? file.name.slice(dot + 1).toLowerCase() : ''
  const videoExt: Record<string, string> = {
    mp4: 'video/mp4',
    m4v: 'video/x-m4v',
    mov: 'video/quicktime',
    qt: 'video/quicktime',
    webm: 'video/webm',
    mkv: 'video/x-matroska',
    avi: 'video/x-msvideo',
    '3gp': 'video/3gpp',
    '3g2': 'video/3gpp2',
    mpeg: 'video/mpeg',
    mpg: 'video/mpeg',
    ogv: 'video/ogg',
  }
  const imageExt: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    heic: 'image/heic',
    heif: 'image/heif',
    bmp: 'image/bmp',
  }
  const vMime = videoExt[ext]
  if (vMime) return { contentType: vMime, mediaType: 'video' }
  const iMime = imageExt[ext]
  if (iMime) return { contentType: iMime, mediaType: 'image' }
  return null
}

interface ApiOptions extends RequestInit {
  requireAuth?: boolean
}

export interface ApiError extends Error {
  status?: number
  code?: string
  data?: any
}

class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  private async request<T>(
    endpoint: string,
    options: ApiOptions = {}
  ): Promise<T> {
    const { requireAuth = false, ...requestOptions } = options

    const url = `${this.baseUrl}${endpoint}`
    const config: RequestInit = {
      ...requestOptions,
      headers: {
        'Content-Type': 'application/json',
        ...requestOptions.headers,
      },
    }

    // Add auth header if required
    if (requireAuth) {
      const { token } = useAuthStore.getState()
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        }
      }
    }

    try {
      const response = await fetch(url, config)

      // Handle token expiration
      if (response.status === 401 && requireAuth) {
        this.handleTokenExpiration()
        throw new Error('Session expired')
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Request failed' }))
        const httpError = new Error(errorData.message || `HTTP ${response.status}`) as ApiError
        // Additive: expose the backend error code/status/body for callers that need them
        httpError.status = response.status
        if (errorData && typeof errorData.code === 'string') httpError.code = errorData.code
        httpError.data = errorData
        throw httpError
      }

      return await response.json()
    } catch (error) {
      if (error instanceof Error && error.message === 'Session expired') {
        throw error
      }
      const wrapped = new Error(error instanceof Error ? error.message : 'Request failed') as ApiError
      if (error instanceof Error) {
        const src = error as ApiError
        if (src.status !== undefined) wrapped.status = src.status
        if (src.code !== undefined) wrapped.code = src.code
        if (src.data !== undefined) wrapped.data = src.data
      }
      throw wrapped
    }
  }

  private handleTokenExpiration() {
    const { logout } = useAuthStore.getState()

    // Clear auth state
    logout()

    // Planner pages (/p/<slug>) never send the customer away to Mila's sign-in: their booking steps ask for the
    // phone and code inline, so a lapsed session just signs out quietly.
    if (typeof window !== 'undefined' && /^(\/[a-z]{2})?\/p\//.test(window.location.pathname)) return

    // Show session expired message
    toast.error('Your session has expired. Please log in again.', {
      duration: 5000,
      position: 'top-center',
    })

    // Redirect to login (using window.location to ensure it works from anywhere)
    window.location.href = '/auth/signin'
  }

  // GET request
  async get<T>(endpoint: string, options?: ApiOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' })
  }

  // POST request
  async post<T>(endpoint: string, data?: any, options?: ApiOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  // PUT request
  async put<T>(endpoint: string, data?: any, options?: ApiOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  // DELETE request
  async delete<T>(endpoint: string, options?: ApiOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' })
  }
}

export const apiClient = new ApiClient(API_URL)

// Export commonly used API functions
export const api = {
  // Auth endpoints
  sendOTP: (phone: string) => apiClient.post('/auth/send-otp', { phone }),
  verifyOTP: (otp: string, verificationToken: string) => apiClient.post('/auth/verify-otp', { otp, verificationToken }),

  // Public endpoints (no auth required)
  getFAQs: () => apiClient.get(`/faqs`),
  getPartners: () => apiClient.get('/partners'),
  getCategories: () =>
    apiClient.get(
      `/categories?${new URLSearchParams({ [WEBSITE_ONLY_QUERY_PARAM]: 'true' }).toString()}`
    ),
  getBlogs: ({ page = 1, limit = 20, search, is_popular = '' }: { page?: number; limit?: number; search?: string; is_popular?: string }) => {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    })

    if (search) {
      params.append('search', search)
    }
    if (is_popular) {
      params.append('is_popular', is_popular)
    }

    return apiClient.get(`/blog/articles?${params.toString()}`)
  },
  getBlog: (id: string) => apiClient.get(`/blog/articles/${id}`),
  getAllCategories: (params?: Record<string, string>) => {
    const merged = { [WEBSITE_ONLY_QUERY_PARAM]: 'true', ...(params || {}) }
    return apiClient.get(`/categories?${new URLSearchParams(merged).toString()}`)
  },
  getTags: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params).toString()}` : ''
    return apiClient.get(`/tags${query}`)
  },
  getAllHomeCategories: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params).toString()}` : ''
    return apiClient.get(`/homescreen-cats${query}`)
  },
  getVendors: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params).toString()}` : ''
    return apiClient.get(`/vendors${query}`)
  },
  getItems: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params).toString()}` : ''
    // Check if user is authenticated to include auth token
    const { token } = useAuthStore.getState()
    return apiClient.get(`/items${query}`, { requireAuth: !!token })
  },
  getVenues: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params).toString()}` : ''
    // Check if user is authenticated to include auth token
    const { token } = useAuthStore.getState()
    return apiClient.get(`/venues${query}`, { requireAuth: !!token })
  },
  getVenueShiftTimeslots: (venueId: string, date: string) =>
    apiClient.get(`/venues/${venueId}/shift-timeslots?date=${encodeURIComponent(date)}`),
  getItem: (id: string) => {
    // Check if user is authenticated to include auth token
    const { token } = useAuthStore.getState()
    return apiClient.get(`/items/${id}`, { requireAuth: !!token })
  },
  getCategory: (id: string) =>
    apiClient.get(
      `/categories/${id}?${new URLSearchParams({ [WEBSITE_ONLY_QUERY_PARAM]: 'true' }).toString()}`
    ),
  getItemsByCategory: (categoryId: string) => {
    // Check if user is authenticated to include auth token
    const { token } = useAuthStore.getState()
    return apiClient.get(`/items/category/${categoryId}`, { requireAuth: !!token })
  },
  getSubcategory: (id: string) => apiClient.get(`/subcategories/${id}`),
  getVirtualCategory: (id: string) =>
    apiClient.get(
      `/virtual-categories/${id}?${new URLSearchParams({ [WEBSITE_ONLY_QUERY_PARAM]: 'true' }).toString()}`
    ),
  getAllVirtualCategory: (id: string, params?: Record<string, string>) => {
    const merged = { [WEBSITE_ONLY_QUERY_PARAM]: 'true', ...(params || {}) }
    return apiClient.get(`/virtual-categories/${id}?${new URLSearchParams(merged).toString()}`)
  },
  getVirtualCategoryCategories: (id: string) =>
    apiClient.get(
      `/virtual-categories/${id}/categories?${new URLSearchParams({ [WEBSITE_ONLY_QUERY_PARAM]: 'true' }).toString()}`
    ),
  // Physical Spaces
  getPhysicalSpaces: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params).toString()}` : ''
    return apiClient.get(`/physical-spaces${query}`)
  },
  getPhysicalSpace: (id: string) => apiClient.get(`/physical-spaces/${id}`),

  // Search endpoints
  searchItems: (query: string) => {
    const { token } = useAuthStore.getState()
    return apiClient.get(`/items?search=${encodeURIComponent(query)}`, { requireAuth: !!token })
  },
  searchVendors: (query: string) => {
    const { token } = useAuthStore.getState()
    return apiClient.get(`/vendors?search=${encodeURIComponent(query)}`, { requireAuth: !!token })
  },
  searchVenues: (query: string) => {
    const { token } = useAuthStore.getState()
    return apiClient.get(`/venues?search=${encodeURIComponent(query)}`, { requireAuth: !!token })
  },

  // Protected endpoints (require auth)
  getProfile: () => apiClient.get('/auth/profile', { requireAuth: true }),
  updateProfile: (data: any) => apiClient.put('/auth/profile', data, { requireAuth: true }),

  // Chat
  getMyChatConversations: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params).toString()}` : ''
    return apiClient.get(`/chat/customer/conversations${query}`, { requireAuth: true })
  },
  startConversationWithVendor: (vendorId: string) =>
    apiClient.post('/chat/customer/conversations', { vendor_id: vendorId }, { requireAuth: true }),
  getChatMessages: (conversationId: string, params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params).toString()}` : ''
    return apiClient.get(`/chat/customer/conversations/${conversationId}/messages${query}`, { requireAuth: true })
  },
  sendChatMessage: (conversationId: string, data: any) =>
    apiClient.post(`/chat/customer/conversations/${conversationId}/messages`, data, { requireAuth: true }),
  markChatRead: (conversationId: string) =>
    apiClient.post(`/chat/customer/conversations/${conversationId}/read`, {}, { requireAuth: true }),
  /**
   * Upload a chat media file (image or video) directly to S3 via a presigned URL.
   * Returns { url, key, mediaType } where url is the public S3/CloudFront URL.
   * The file never passes through the Lambda backend, avoiding the 6 MB body limit.
   */
  uploadChatMedia: async (file: File): Promise<{ url: string; key: string; mediaType: 'image' | 'video' }> => {
    const { token } = useAuthStore.getState()

    const inferred = inferChatMediaFromFile(file)
    if (!inferred) {
      throw new Error('Could not recognize this file. Choose a photo or video (for example JPG, PNG, HEIC, MP4, MOV).')
    }
    const { contentType, mediaType } = inferred

    // 1. Request a presigned upload URL from the backend
    const presignRes = await fetch(`${API_URL}/uploads/presigned-chat-media`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ filename: file.name || 'upload.bin', contentType, folder: 'chat-messages' }),
    })

    if (!presignRes.ok) {
      const err = await presignRes.json().catch(() => ({ message: 'Failed to get upload URL' }))
      throw new Error(err.message || 'Failed to get upload URL')
    }

    const { data: presignData } = await presignRes.json()

    // 2. PUT the file directly to S3
    const s3Res = await fetch(presignData.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: file,
    })

    if (!s3Res.ok) {
      throw new Error('Failed to upload file to S3')
    }

    return { url: presignData.finalUrl, key: presignData.key, mediaType }
  },

  // Orders
  getOrders: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params).toString()}` : ''
    return apiClient.get(`/orders${query}`, { requireAuth: true })
  },
  getOrder: (id: string) => apiClient.get(`/orders/${id}`, { requireAuth: true }),
  createOrder: (data: any) => apiClient.post('/orders', data, { requireAuth: true }),
  cancelOrder: (orderId: string, data: { reason_code: string; reason_text?: string }) =>
    apiClient.post(`/orders/${orderId}/cancel`, data, { requireAuth: true }),
  calculateVenueOrderFees: (data: any) => apiClient.post('/venue-orders/calculate-fees', data, { requireAuth: true }),
  createVenueOrder: (data: any) => apiClient.post('/venue-orders', data, { requireAuth: true }),
  updateOrder: (id: string, data: any) => apiClient.put(`/orders/${id}`, data, { requireAuth: true }),
  getStatusCatalog: () => apiClient.get('/orders/status-catalog', { requireAuth: false }),
  // kind 'bookings': only orders with a venue booking (the website's account lists bookings; services are in the app).
  getMyOrders: (filter: 'running' | 'history', page = 1, limit = 20, kind?: 'bookings') =>
    apiClient.get(`/orders/my-orders?filter=${filter}&page=${page}&limit=${limit}${kind ? `&kind=${kind}` : ''}`, { requireAuth: true }),

  // Venue Bookings
  getVenueBookings: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params).toString()}` : ''
    return apiClient.get(`/venue-bookings/my-bookings${query}`, { requireAuth: true })
  },
  getVenueBooking: (id: string) => apiClient.get(`/venue-bookings/${id}`, { requireAuth: true }),
  createVenueBooking: (data: any) => apiClient.post('/venue-bookings', data, { requireAuth: true }),
  cancelVenueBooking: (id: string, reason?: string) => apiClient.put(`/venue-bookings/${id}/cancel`, { reason }, { requireAuth: true }),

  // Cart
  getCart: () => apiClient.get('/cart', { requireAuth: true }),
  addToCart: (data: any) => apiClient.post('/cart', data, { requireAuth: true }),
  updateCartItem: (id: string, data: any) => apiClient.put(`/cart/${id}`, data, { requireAuth: true }),
  removeFromCart: (id: string) => apiClient.delete(`/cart/${id}`, { requireAuth: true }),
  clearCart: () => apiClient.delete('/cart', { requireAuth: true }),

  // Reviews
  getPendingReviews: () => apiClient.get('/reviews/pending', { requireAuth: true }),
  createReview: (data: any) => apiClient.post('/reviews', data, { requireAuth: true }),
  getOrderReviews: (orderId: string) => apiClient.get(`/reviews/order/${orderId}`, { requireAuth: true }),
  getVendorReviews: (vendorId: string, params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params).toString()}` : ''
    return apiClient.get(`/reviews/vendor/${vendorId}${query}`)
  },
  getItemReviews: (itemId: string, params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params).toString()}` : ''
    return apiClient.get(`/reviews/item/${itemId}${query}`)
  },
  getVenueReviews: (venueId: string, params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params).toString()}` : ''
    return apiClient.get(`/reviews/venue/${venueId}${query}`)
  },
  getPhysicalSpaceReviews: (physicalSpaceId: string, params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params).toString()}` : ''
    return apiClient.get(`/reviews/physical-space/${physicalSpaceId}${query}`)
  },

  // Contact Us (public endpoint - no auth required)
  submitContactMessage: (data: any) => apiClient.post('/contact-us', data, { requireAuth: false }),

  // Vendor Join Request (public endpoint - no auth required)
  submitVendorRequest: (data: any) => apiClient.post('/vendor-requests', data, { requireAuth: false }),

  // Favorites
  toggleFavorite: (data: { favoriteable_id: string; favoriteable_type: 'item' | 'vendor' }) =>
    apiClient.post('/favorites/toggle', data, { requireAuth: true }),
  getFavorites: () => apiClient.get('/favorites', { requireAuth: true }),
  checkFavoriteStatus: (favoriteable_id: string, favoriteable_type: 'item' | 'vendor') => {
    const query = `?favoriteable_id=${favoriteable_id}&favoriteable_type=${favoriteable_type}`
    return apiClient.get(`/favorites/check${query}`, { requireAuth: true })
  },

  // Settings - Lookup data (public endpoint - no auth required)
  getLookupData: (appType: 'user' | 'vendor', platform: 'android' | 'ios' | 'web', version: string) => {
    return apiClient.get('/settings/lookup/data', {
      requireAuth: false,
      headers: {
        'app_type': appType,
        'platform': platform,
        'version': version
      }
    })
  },

  // Image utilities
  getPresignedImageUrl: (imagePath: string, expiresIn = 3600) =>
    apiClient.post('/upload/presigned-view-url', { imagePath, expiresIn }, { requireAuth: false }),

  // Wallet
  getWalletBalance: () => apiClient.get('/wallet/balance', { requireAuth: true }),
  getWalletTransactions: (page = 1, limit = 20) =>
    apiClient.get(`/wallet/transactions?page=${page}&limit=${limit}`, { requireAuth: true }),
  calculateWalletPayment: (orderAmount: number) =>
    apiClient.post('/wallet/calculate-payment', { order_amount: orderAmount }, { requireAuth: true }),

  // Vendor-scoped cashback (spendable only at the granting vendor)
  getVendorCashbackBalances: () =>
    apiClient.get('/vendor-cashback/balances', { requireAuth: true }),
  getVendorCashbackTransactions: (limit = 20, offset = 0, vendorId?: string) =>
    apiClient.get(
      `/vendor-cashback/transactions?limit=${limit}&offset=${offset}${vendorId ? `&vendor_id=${vendorId}` : ''}`,
      { requireAuth: true }
    ),

  // Promo Codes
  getAvailablePromoCodes: () => apiClient.get('/promo-codes/available', { requireAuth: true }),
  validatePromoCode: (code: string, orderAmount: number) =>
    apiClient.post('/promo-codes/validate', { code, order_amount: orderAmount }, { requireAuth: true }),

  // Availability
  checkVenueAvailability: (venueId: string, data: any) =>
    apiClient.post(`/venues/${venueId}/check-availability`, data, { requireAuth: true }),
  // Real time slots for an hours-based venue on a given date (YYYY-MM-DD).
  // Returns { success, slots: [{ start_time:"HH:MM", end_time, available, capacity, min_guests, max_guests }] }.
  // Day-based venues return slots: [].
  getVenueTimeslots: (venueId: string, date: string) =>
    apiClient.get(`/venues/${venueId}/timeslots?date=${encodeURIComponent(date)}`),
} 